/**
 * Progressive rendering for long non-virtualized lists (#1144 M5).
 *
 * The nested saved-view ledger renders variable-height rows (wrapped text,
 * expand/collapse, inline editors) in normal page flow — the shapes that make
 * fixed-size windowing libraries wrong without measurement machinery. The
 * cost that actually hurt was the INITIAL render: "Show All" mounts 600+
 * bills × several rows in one commit. This hook renders a window and grows
 * it just before the user reaches the bottom, so DOM builds as scrolled
 * instead of up front. Already-rendered rows are kept (no unmount-behind):
 * scrolling back up is free, and grown DOM is cheap compared to mounting it
 * all at once.
 *
 * Usage:
 *   const { visibleCount, sentinelRef } = useProgressiveRender(items.length, resetKey);
 *   items.slice(0, visibleCount).map(...)
 *   {visibleCount < items.length && <div ref={sentinelRef} />}
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const STEP = 60;

export function useProgressiveRender(
  totalCount: number,
  /** Changing this resets the window (new search, sort, or view). */
  resetKey: unknown,
  step: number = STEP
) {
  const [visibleCount, setVisibleCount] = useState(step);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVisibleCount(step);
    // resetKey is the dependency that matters; step is constant per call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const grow = useCallback(
    () => setVisibleCount((n) => Math.min(n + step, totalCount)),
    [step, totalCount]
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || visibleCount >= totalCount) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      // Test environments (jsdom): render everything rather than trap the list.
      setVisibleCount(totalCount);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) grow();
      },
      // Start growing well before the sentinel is visible, so scrolling never hits a wall.
      { rootMargin: '600px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [grow, visibleCount, totalCount]);

  return {
    visibleCount: Math.min(visibleCount, totalCount),
    sentinelRef,
    hasMore: visibleCount < totalCount,
  };
}
